import React, { useMemo, useRef } from 'react';

import isNumber from 'lodash/isNumber';

import {
  fallbackToScientificNotation,
  getChartDropdownOptions,
  getPipelineChartLink,
} from '../../../utils/chart_utils/chart_utils';

import { dataArrayAsDataSet, formatSingleLineChart, getParsedChartPoint } from '../helpers/dataset.helpers';

import { formatChartDate } from '../../../utils/date_utils';

import useCSVChartData from '../helpers/useCSVChartData';
import usePrometheusQuery from '../../../utils/hooks/use_prometheus_query';
import { useRefDimensions } from '../d3-charts/d3-helpers';

import { ChartProvider } from '../ChartContext';
import { ChartWrapper } from '../d3-charts/ChartWrapper';
import MultiAverageDetailPane from '../details/MultiAverageDetailPane';

import LineChart from '../d3-charts/line/D3LineChart';

import COLORS from '../../../constants/colors.constants';

export const useRequestPerSecondData = ({
  deploymentID, 
  startTimestamp, 
  endTimestamp, 
  step
}) => {
  const prometheusResponse = usePrometheusQuery(
    !deploymentID
      ? null
      : `sum by (deploy_id) (rate(latency_histogram_ns_count{deploy_id="${deploymentID}"}[${step}s]))`,
    {
      startTimestamp,
      endTimestamp,
      step,
      queryKey: `request-rate-prometheus-${deploymentID}`,
      transformData: data => data?.map(point => getParsedChartPoint(point[1], point[0])),
    },
  );

  return {
    chartData: prometheusResponse?.data || [],
    isLoading: prometheusResponse?.isLoading,
    message: prometheusResponse?.error || null,
  }
}

export const RequestPerSecondChart = ({
  datasets,
  csvData,
  labelFormatString,
  message,
  pipelineID,
  pipelineVersion,
  chartID,
  startDate,
  endDate,
  isLoading = false,
  isEnlarged = false,
}) => {
  const chartTitle = 'Requests per second';

  const chartLink = useMemo(() => getPipelineChartLink({
    pipelineID, pipelineVersion, chartID,
  }), [chartID, pipelineID, pipelineVersion]);

  const dropdownOptions = getChartDropdownOptions({
    chartLink,
    chartTitle,
    csvData,
    pipelineID,
    pipelineVersion,
    isEnlarged,
  })

  const containerRef = useRef();
  const dimensions = useRefDimensions(containerRef)

  return (
    <ChartProvider
      datasets={datasets}
      chartID={chartID}
      dropdownOptions={dropdownOptions}
      prepareDataset={formatSingleLineChart}
    >
      <ChartWrapper
        containerRef={containerRef}
        chartTitle={chartTitle}
        isLoading={isLoading}
        message={message}
        detailRenderer={MultiAverageDetailPane}
      >
        <LineChart
          chartId="request-per-second"
          datasets={datasets}
          xTickFormat={formatChartDate(labelFormatString)}
          yTickFormat={fallbackToScientificNotation}
          numberOfXTicks={6}
          numberOfYTicks={6}
          startDate={startDate}
          endDate={endDate}
          containerHeight={dimensions?.height && dimensions.height > 0 ? dimensions.height : 0}
          containerWidth={dimensions?.width && dimensions.width > 0 ? dimensions.width : 0}
          showPoints
        />
      </ChartWrapper>
    </ChartProvider>
  )
}

const RequestPerSecondChartWithData = (props) => {
  const {
    deploymentID, startTimestamp, endTimestamp, step,
  } = props;

  const {
    chartData,
    isLoading,
    message,
  } = useRequestPerSecondData({
    deploymentID, startTimestamp, endTimestamp, step
  })

  const pointsWithoutNulls = chartData?.length ? chartData.filter(({ y }) => isNumber(y)) : [];
  const chartDataset = dataArrayAsDataSet(pointsWithoutNulls, 'Requests per Second');
  const datasets = [
    {
      ...chartDataset,
      id: 'request-per-second-dataset',
      lineColor: COLORS.brand,
      bgColor: COLORS.border.dark,
      borderColor: COLORS.brand,
    }
  ];

  const csvData = useCSVChartData({
    chartType: 'simple',
    title: 'Requests per Second',
    data: chartData?.length ? chartData : [[]],
  })

  return (
    <RequestPerSecondChart
      {...props}
      datasets={datasets}
      csvData={csvData}
      isLoading={isLoading}
      message={message}
      startDate={new Date(startTimestamp)}
      endDate={new Date(endTimestamp)}
    />
  )
}

export default RequestPerSecondChartWithData;
